import { useEffect, useRef } from 'react';

// Live time-domain audio waveform for the recording bar. Purely presentational:
// the AnalyserNode is a read-only tap on the mic stream (created in useRecorder),
// this component never touches the recorded MediaStream/MediaRecorder itself.
export function Waveform({ analyser, active, paused, className }: {
  analyser: AnalyserNode | null;
  active: boolean; // recording (animate)
  paused: boolean; // frozen/greyed
  className?: string;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Size the canvas backing store to its container, accounting for DPR so
    // the line stays crisp on high-density screens.
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || canvas.offsetWidth || 1;
    const cssHeight = canvas.clientHeight || canvas.offsetHeight || 1;
    canvas.width = Math.max(1, Math.round(cssWidth * dpr));
    canvas.height = Math.max(1, Math.round(cssHeight * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const drawLine = (dataArray: Uint8Array | null, color: string, opacity: number): void => {
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      ctx.globalAlpha = opacity;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();

      const mid = cssHeight / 2;
      if (!dataArray || dataArray.length === 0) {
        ctx.moveTo(0, mid);
        ctx.lineTo(cssWidth, mid);
      } else {
        const step = cssWidth / dataArray.length;
        for (let i = 0; i < dataArray.length; i++) {
          const sample = dataArray[i] ?? 128;
          const v = sample / 128 - 1; // -1..1
          const y = mid + v * mid;
          const x = i * step;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    };

    const primaryColor = 'hsl(var(--primary))';
    const mutedColor = 'hsl(var(--muted-foreground))';

    if (active && !paused && analyser) {
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = (): void => {
        analyser.getByteTimeDomainData(dataArray);
        drawLine(dataArray, primaryColor, 1);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } else {
      // Not actively animating: leave a flat/greyed line drawn (frozen last
      // frame is not retained across re-renders since the canvas was resized
      // above, so draw a muted flat baseline instead).
      drawLine(null, mutedColor, paused ? 0.4 : 0.25);
    }

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [analyser, active, paused]);

  return (
    <canvas
      ref={canvasRef}
      className={className ?? 'h-10 w-full'}
      aria-hidden="true"
    />
  );
}
